import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Autocomplete,
} from "@mui/material";
import TournamentBracket from "../brackets"; // Correct path to your TournamentBracket component
import api from "src/services/api";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useAuthenticate from "src/hooks/useAuthenticate";
import { PDFDocument, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import logo from "../../../../assets/images/branding/logo-torneiofacil.png";

// Styling for the modal, remains unchanged
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  width: "90%", // Increased width for better visibility in modal
  maxWidth: "1150px", // Max width for larger screens
  maxHeight: "90vh", // Set max height to allow scrolling
  display: "flex",
  flexDirection: "column",
};

// Interface for component props, remains unchanged
interface IProps {
  chaves: any;
  tournamentId: any;
  tournamentName: string;
  tournamentLocation: string;
  tournamentDate: string;
  tournamentRules: any[];
}

export default function BasicModal({
  chaves,
  tournamentId,
  tournamentName,
  tournamentDate,
  tournamentLocation,
  tournamentRules,
}: IProps) {
  // State variables for modal visibility, bracket data, PDF generation, and filters
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [bracketsData, setBracketsData] = useState<any>({});
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const { getToken, account } = useAuthenticate();

  // Ref to hold DOM elements of individual brackets for PDF generation
  const bracketRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Filter states
  const [fighterNameFilter, setFighterNameFilter] = useState("");
  const [gymFilter, setGymFilter] = useState("");
  // categoryFilter can be a string (selected category) or null (no selection)
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  function normalizeGymName(name: string): string {
    return (name || "")
      .normalize("NFD") // separa acentos
      .replace(/[\u0300-\u036f]/g, "") // remove acentos
      .toLowerCase()
      .replace(/\u00A0/g, " ") // NBSP -> espaço
      .replace(/\s+/g, " ") // múltiplos espaços -> 1
      .trim();
  }

  // Fetch bracket data when tournamentId changes
  useEffect(() => {
    if (tournamentId) {
      if (!tournamentRules) {
        if (account.id == 34) {
          toast.error("Não foram cadastradas regras para este torneio");
        }
      }
      api
        .get(`administrators/tournaments/${tournamentId}/bracket`)
        .then((res) => {
          setBracketsData(res.data.data || {});
        })
        .catch((error) => {
          console.error("Erro ao buscar dados das chaves:", error);
          toast.error("Erro ao carregar as chaves do torneio.");
        });
    }
  }, [tournamentId]);

  // Function to generate (or re-generate) tournament keys via API
  const generateKeys = () => {
    if (!tournamentId) {
      toast.error("ID do torneio não encontrado.");
      return;
    }

    console.log("->>>>>>>>>>>>>>", chaves);

    api
      .post(`administrators/tournaments/${tournamentId}/bracket`, chaves, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      })
      .then((res) => {
        setBracketsData(res.data.data || {});
        toast.success("Chaves geradas com sucesso!");
      })
      .catch((error) => {
        toast.error("Erro ao gerar chaves.");
        console.error("Erro ao gerar chaves:", error);
      });
  };

  const generateManualFightsPDF = async (
    fightsByCategory: Record<string, { desc: any; fights: Array<any[]> }>,
    tournamentName: string
  ) => {
    const fontBytes = await fetch("/DejaVuSans.ttf").then((r) => r.arrayBuffer());
    const logoBytes = await fetch(logo).then((r) => r.arrayBuffer());

    const A4_W = 595.28;
    const A4_H = 841.89;
    const margin = 25;

    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);
    const fonte = await pdfDoc.embedFont(fontBytes);
    const logoImg = await pdfDoc.embedPng(logoBytes);

    const navy = rgb(0.102, 0.133, 0.322);
    const red = rgb(0.894, 0.125, 0.173);
    const gray500 = rgb(0.42, 0.455, 0.565);
    const gray300 = rgb(0.769, 0.788, 0.847);
    const gray50 = rgb(0.969, 0.973, 0.984);
    const white = rgb(1, 1, 1);

    const contentW = A4_W - 2 * margin;
    const colNumW = 25;
    const colNameW = contentW * 0.52;
    const colGymW = contentW - colNumW - colNameW;
    const rowH = 14;
    const headerRowH = 16;
    const fontSize = 8;
    const headerFontSize = 7;

    let page = pdfDoc.addPage([A4_W, A4_H]);
    let curY = A4_H - margin;

    const addNewPage = () => {
      page = pdfDoc.addPage([A4_W, A4_H]);
      curY = A4_H - margin;
    };

    const drawPageHeader = () => {
      const smallLogoW = 95;
      const smallLogoH = (logoImg.height / logoImg.width) * smallLogoW;
      const headerH = Math.max(smallLogoH, 28);

      page.drawImage(logoImg, {
        x: margin,
        y: curY - smallLogoH,
        width: smallLogoW,
        height: smallLogoH,
      });

      const titleSize = 11;
      const titleW = fonte.widthOfTextAtSize(tournamentName, titleSize);
      page.drawText(tournamentName, {
        x: (A4_W - titleW) / 2,
        y: curY - 12,
        size: titleSize,
        font: fonte,
        color: navy,
      });

      const subtitle = "LUTAS CAÇADAS";
      const subW = fonte.widthOfTextAtSize(subtitle, 8);
      page.drawText(subtitle, {
        x: (A4_W - subW) / 2,
        y: curY - 24,
        size: 8,
        font: fonte,
        color: gray500,
      });

      curY -= headerH + 6;

      page.drawLine({
        start: { x: margin, y: curY },
        end: { x: A4_W - margin, y: curY },
        thickness: 1.5,
        color: red,
      });
      curY -= 16;
    };

    const drawTableHeader = () => {
      page.drawRectangle({
        x: margin,
        y: curY - headerRowH,
        width: contentW,
        height: headerRowH,
        color: navy,
      });

      const headers = ["Nº", "ATLETA", "ACADEMIA"];
      const colWidths = [colNumW, colNameW, colGymW];
      let hx = margin;
      headers.forEach((h, i) => {
        const hw = fonte.widthOfTextAtSize(h, headerFontSize);
        const cellCenter = hx + (colWidths[i] - hw) / 2;
        page.drawText(h, {
          x: cellCenter,
          y: curY - headerRowH + 5,
          size: headerFontSize,
          font: fonte,
          color: white,
        });
        hx += colWidths[i];
      });
      curY -= headerRowH;
    };

    drawPageHeader();

    let globalFightIdx = 0;

    for (const [categoria, { desc, fights }] of Object.entries(fightsByCategory)) {
      if (fights.length === 0) continue;

      const spaceNeeded = 40 + headerRowH + rowH * 2;
      if (curY - spaceNeeded < 50) {
        addNewPage();
        drawPageHeader();
      }

      // Cabeçalho da categoria
      const catClean = categoria.replace(/-parte\d+/gi, "");
      const catName = catClean.toUpperCase();
      const catRules = ` ${desc.ageDescription}, ${desc.pesoDescription}, tempo de luta: ${desc.fightTime} min`;

      page.drawText(catName, {
        x: margin,
        y: curY,
        size: 9,
        font: fonte,
        color: navy,
      });
      const catNameW = fonte.widthOfTextAtSize(catName, 9);
      page.drawText(catRules, {
        x: margin + catNameW,
        y: curY,
        size: 7,
        font: fonte,
        color: gray500,
      });
      curY -= 10;

      drawTableHeader();

      fights.forEach((fight, fightIdx) => {
        if (curY - rowH * 2 < 50) {
          addNewPage();
          drawPageHeader();
          drawTableHeader();
        }

        const bgColor = fightIdx % 2 === 0 ? gray50 : white;

        fight.forEach((fighter, rowIdx) => {
          if (!fighter) return;

          page.drawRectangle({
            x: margin,
            y: curY - rowH,
            width: contentW,
            height: rowH,
            color: bgColor,
          });

          page.drawLine({
            start: { x: margin, y: curY - rowH },
            end: { x: margin + contentW, y: curY - rowH },
            thickness: rowIdx === fight.length - 1 ? 0.8 : 0.3,
            color: gray300,
          });

          // Separadores de coluna
          page.drawLine({
            start: { x: margin + colNumW, y: curY },
            end: { x: margin + colNumW, y: curY - rowH },
            thickness: 0.3,
            color: gray300,
          });
          page.drawLine({
            start: { x: margin + colNumW + colNameW, y: curY },
            end: { x: margin + colNumW + colNameW, y: curY - rowH },
            thickness: 0.3,
            color: gray300,
          });

          const cellY = curY - rowH + 4;

          // Nº (apenas na primeira linha de cada luta)
          if (rowIdx === 0) {
            const lutaNum = (globalFightIdx + fightIdx + 1).toString();
            const numW = fonte.widthOfTextAtSize(lutaNum, fontSize);
            page.drawText(lutaNum, {
              x: margin + (colNumW - numW) / 2,
              y: cellY,
              size: fontSize,
              font: fonte,
              color: navy,
            });
          }

          // Atleta (nome - faixa - peso)
          const nameText = `${fighter.name.toUpperCase()} - ${(fighter.belt || "").toUpperCase()} - ${((fighter.weight || "").split(";")[0] || "").toUpperCase()}`;
          const truncName = nameText.length > 45 ? nameText.slice(0, 42) + "..." : nameText;
          page.drawText(truncName, {
            x: margin + colNumW + 5,
            y: cellY,
            size: fontSize,
            font: fonte,
            color: rgb(0, 0, 0),
          });

          // Academia
          const gymRaw = (fighter.gym || "").toUpperCase();
          const gymText = gymRaw.length > 30 ? gymRaw.slice(0, 27) + "..." : gymRaw;
          page.drawText(gymText, {
            x: margin + colNumW + colNameW + 5,
            y: cellY,
            size: fontSize,
            font: fonte,
            color: gray500,
          });

          curY -= rowH;
        });
      });

      globalFightIdx += fights.length;
      curY -= 24;
    }

    // Footers
    const allPages = pdfDoc.getPages();
    const total = allPages.length;
    allPages.forEach((p, i) => {
      p.drawLine({ start: { x: margin, y: 25 }, end: { x: A4_W - margin, y: 25 }, thickness: 1, color: navy });
      const lt = `${tournamentLocation} , ${tournamentDate.split("-").reverse().join("/")}`;
      p.drawText(lt, { x: margin, y: 13, size: 7, font: fonte, color: gray500 });
      const rt = `www.torneiofacil.com.br ${i + 1}/${total}`;
      const rw = fonte.widthOfTextAtSize(rt, 7);
      p.drawText(rt, { x: A4_W - margin - rw, y: 13, size: 7, font: fonte, color: gray500 });
    });

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `LUTAS CAÇADAS - ${tournamentName}.pdf`;
    link.click();
  };

  const hasCasamentoManual = (text: string): boolean => {
    if (!text) return false;
    return /casamento manual/i.test(text);
  };

  const generateManualFights = async () => {
    const fightsByCategory: Record<string, { desc: any; fights: Array<any[]> }> = {};

    Object.entries(filteredAndSplitData).forEach(([category, data]) => {
      if (
        data &&
        typeof data === "object" &&
        Array.isArray((data as any).matchStructure)
      ) {
        const matchStructure = (data as any).matchStructure;
        const fights: any[][] = [];

        for (let i = 0; i < matchStructure.length; i += 2) {
          const fight: any[] = [];

          if (matchStructure[i]) {
            fight.push({
              name: matchStructure[i][0]?.lutador?.name || "N/A",
              gym: matchStructure[i][0]?.lutador?.gym || "N/A",
              belt: matchStructure[i][0]?.lutador?.belt || "",
              weight: matchStructure[i][0]?.lutador?.weight || "N/A",
              marriedFight: hasCasamentoManual(category),
            });
          }

          if (matchStructure[i + 1]) {
            fight.push({
              name: matchStructure[i + 1][0]?.lutador?.name || "N/A",
              gym: matchStructure[i + 1][0]?.lutador?.gym || "N/A",
              belt: matchStructure[i + 1][0]?.lutador?.belt || "",
              weight: matchStructure[i + 1][0]?.lutador?.weight || "N/A",
              marriedFight: hasCasamentoManual(category),
            });
          }

          if (fight.length > 0) {
            fights.push(fight);
          }
        }

        if (fights.length > 0) {
          const desc = getDescriptionFromCategory(category);
          fightsByCategory[category] = { desc, fights };
        }
      }
    });

    await generateManualFightsPDF(fightsByCategory, tournamentName);
  };

  const generateGymPoints = async () => {
    if (!tournamentId) {
      toast.error("ID do torneio não encontrado.");
      return;
    }

    const allFighters: any[] = [];
    const gymMap = new Map<string, string>();

    chaves.forEach((category: { figthers: any[] }) => {
      category.figthers.forEach((fighter) => {
        allFighters.push(fighter);
        const normalized = normalizeGymName(fighter.gym);
        if (normalized && !gymMap.has(normalized)) {
          gymMap.set(normalized, fighter.gym.trim());
        }
      });
    });

    const gyms = Array.from(gymMap.keys()).sort((a, b) =>
      gymMap.get(a)!.localeCompare(gymMap.get(b)!, "pt-BR")
    );

    const gymCountMap: Record<string, number> = {};
    allFighters.forEach((fighter) => {
      const normalized = normalizeGymName(fighter.gym);
      if (normalized) gymCountMap[normalized] = (gymCountMap[normalized] || 0) + 1;
    });

    const fontBytes = await fetch("/DejaVuSans.ttf").then((r) => r.arrayBuffer());
    const logoBytes = await fetch(logo).then((r) => r.arrayBuffer());

    const A4_W = 595.28;
    const A4_H = 841.89;
    const margin = 25;

    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);
    const fonte = await pdfDoc.embedFont(fontBytes);
    const logoImg = await pdfDoc.embedPng(logoBytes);

    const navy = rgb(0.102, 0.133, 0.322);
    const red = rgb(0.894, 0.125, 0.173);
    const gray500 = rgb(0.42, 0.455, 0.565);
    const gray300 = rgb(0.769, 0.788, 0.847);
    const gray50 = rgb(0.969, 0.973, 0.984);
    const white = rgb(1, 1, 1);
    const gold = rgb(0.85, 0.65, 0.13);
    const silver = rgb(0.75, 0.75, 0.75);
    const bronze = rgb(0.72, 0.45, 0.20);

    const contentW = A4_W - 2 * margin;
    const colW = (contentW - 10) / 2; // 2 colunas com gap
    const blockH = 75; // altura do bloco por academia

    let page = pdfDoc.addPage([A4_W, A4_H]);
    let curY = A4_H - margin;

    const addNewPage = () => {
      page = pdfDoc.addPage([A4_W, A4_H]);
      curY = A4_H - margin;
    };

    const drawHeader = () => {
      const lw = 75;
      const lh = (logoImg.height / logoImg.width) * lw;
      page.drawImage(logoImg, { x: margin, y: curY - lh, width: lw, height: lh });
      const tw = fonte.widthOfTextAtSize(tournamentName, 11);
      page.drawText(tournamentName, { x: (A4_W - tw) / 2, y: curY - 12, size: 11, font: fonte, color: navy });
      const sub = "PONTUAÇÃO GERAL DAS ACADEMIAS";
      const sw = fonte.widthOfTextAtSize(sub, 8);
      page.drawText(sub, { x: (A4_W - sw) / 2, y: curY - 24, size: 8, font: fonte, color: gray500 });
      curY -= Math.max(lh, 28) + 6;
      page.drawLine({ start: { x: margin, y: curY }, end: { x: A4_W - margin, y: curY }, thickness: 1.5, color: red });
      curY -= 16;
    };

    const drawGymBlock = (gymKey: string | null, x: number, y: number) => {
      const gymName = gymKey ? (gymMap.get(gymKey) || "") : "";
      const count = gymKey ? (gymCountMap[gymKey] || 0) : 0;

      // Nome da academia (header do bloco)
      page.drawRectangle({ x, y: y - 18, width: colW, height: 18, color: gymKey ? navy : gray300 });
      if (gymKey) {
        const gymText = `${gymName.toUpperCase()} (${count})`;
        // Ajustar tamanho da fonte para caber no bloco
        let fs = 7;
        while (fonte.widthOfTextAtSize(gymText, fs) > colW - 6 && fs > 4) fs -= 0.5;
        const gw = fonte.widthOfTextAtSize(gymText, fs);
        page.drawText(gymText, { x: x + (colW - gw) / 2, y: y - 13, size: fs, font: fonte, color: white });
      }

      // Linhas 1º, 2º, 3º
      const podium = [
        { label: "1º", color: gold },
        { label: "2º", color: silver },
        { label: "3º", color: bronze },
      ];
      let ly = y - 18;
      podium.forEach((p) => {
        ly -= 19;
        page.drawRectangle({ x, y: ly, width: colW, height: 19, color: gray50 });
        page.drawLine({ start: { x, y: ly }, end: { x: x + colW, y: ly }, thickness: 1, color: gray300 });
        page.drawText(p.label, { x: x + 6, y: ly + 6, size: 9, font: fonte, color: p.color });
      });

      // Borda do bloco
      page.drawRectangle({ x, y: ly, width: colW, height: y - ly, borderColor: gray300, borderWidth: 0.5, color: undefined as any, opacity: 0 });
    };

    drawHeader();

    // Renderizar academias reais em 2 colunas
    for (let i = 0; i < gyms.length; i += 2) {
      if (curY - blockH < 40) {
        addNewPage();
        drawHeader();
      }

      drawGymBlock(gyms[i], margin, curY);
      if (i + 1 < gyms.length) {
        drawGymBlock(gyms[i + 1], margin + colW + 10, curY);
      } else {
        // Última academia ímpar: bloco vazio à direita
        drawGymBlock(null, margin + colW + 10, curY);
      }

      curY -= blockH + 6;
    }

    // Completar a página atual com blocos vazios
    while (curY - blockH >= 40) {
      drawGymBlock(null, margin, curY);
      drawGymBlock(null, margin + colW + 10, curY);
      curY -= blockH + 6;
    }

    // Footers
    const allPages = pdfDoc.getPages();
    const total = allPages.length;
    allPages.forEach((p, idx) => {
      p.drawLine({ start: { x: margin, y: 25 }, end: { x: A4_W - margin, y: 25 }, thickness: 1, color: navy });
      const lt = `${tournamentLocation} , ${tournamentDate.split("-").reverse().join("/")}`;
      p.drawText(lt, { x: margin, y: 13, size: 7, font: fonte, color: gray500 });
      const rt = `www.torneiofacil.com.br ${idx + 1}/${total}`;
      const rw = fonte.widthOfTextAtSize(rt, 7);
      p.drawText(rt, { x: A4_W - margin - rw, y: 13, size: 7, font: fonte, color: gray500 });
    });

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `ACADEMIAS - ${tournamentName}.pdf`;
    link.click();
  };

  // Function to generate a PDF of the currently displayed (filtered) brackets
  const generatePdf = async () => {
    setIsGeneratingPdf(true);
    const toastId = toast.loading("Gerando PDF das chaves...", {
      progress: 0,
    });

    const doc = new jsPDF("l", "mm", "a4"); // Landscape A4
    let firstPage = true;

    // Use filteredAndSplitData for PDF generation so only visible brackets are included
    const processedBracketsForPdf = sortCategoriesForPdf(
      Object.entries(filteredAndSplitData)
    );
    const totalBrackets = processedBracketsForPdf?.length;
    let processedCount = 0;
    const startTime = Date.now();

    // Helper to format remaining time
    const formatTime = (ms: number) => {
      const seconds = Math.floor(ms / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return minutes > 0
        ? `${minutes} min ${remainingSeconds} seg`
        : `${remainingSeconds} seg`;
    };

    // Loop through each bracket to generate its image and add to PDF
    for (const [key, data] of processedBracketsForPdf) {
      const input = bracketRefs.current[key]; // Use the key directly
      if (input) {
        if (!firstPage) {
          doc.addPage(); // Add new page for each bracket (after the first)
        } else {
          firstPage = false;
        }

        try {
          const canvas = await html2canvas(input, { scale: 1 }); // Render HTML to canvas, increased scale for better resolution
          const imgData = canvas.toDataURL("image/jpeg", 1); // Convert canvas to jpeg data URL with full quality

          const imgWidth = 297; // A4 width in mm
          const pageHeight = 210; // A4 height in mm
          const imgHeight = (canvas.height * imgWidth) / canvas.width; // Calculate image height to maintain aspect ratio
          let heightLeft = imgHeight;
          let position = 0;

          doc.addImage(imgData, "jpeg", 0, position, imgWidth, imgHeight); // Add image to PDF
          heightLeft -= pageHeight;

          // Handle cases where the bracket content is taller than one A4 page
          while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            doc.addPage();
            doc.addImage(imgData, "jpeg", 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }
        } catch (error) {
          console.error("Erro ao gerar canvas para", key, error);
          toast.update(toastId, {
            render: `Erro ao gerar PDF para ${key}.`,
            type: "error",
            isLoading: false,
            autoClose: 5000,
            theme: "colored",
          });
          setIsGeneratingPdf(false);
          return;
        }
      }
      processedCount++;
      // Update toast progress and estimated time
      const elapsedTime = Date.now() - startTime;
      const avgTimePerBracket = elapsedTime / processedCount;
      const remainingBrackets = totalBrackets - processedCount;
      const estimatedRemainingTime = avgTimePerBracket * remainingBrackets;

      toast.update(toastId, {
        render: `Gerando PDF: ${processedCount}/${totalBrackets} chaves (${formatTime(
          estimatedRemainingTime
        )} restantes)...`,
        progress: processedCount / totalBrackets,
      });
    }

    doc.save(`${tournamentName}_chaves.pdf`); // Save the generated PDF
    setIsGeneratingPdf(false);
    toast.update(toastId, {
      render: "PDF das chaves gerado com sucesso!",
      type: "success",
      autoClose: 5000,
      isLoading: false,
      theme: "colored",
    });
  };

  const generatePdfWithImage = async () => {
    // Carregar imagem de fundo
    const imgBytes = await fetch("/chave_limpa.jpg").then((res) =>
      res.arrayBuffer()
    );
    // Carregar fonte
    const fontBytes = await fetch("/DejaVuSans.ttf").then((res) =>
      res.arrayBuffer()
    );
    // Carregar logo usando o import
    const logoBytes = await fetch(logo).then((res) => res.arrayBuffer());

    // Tamanho da folha A4 em pontos (landscape)
    const A4_WIDTH = 841.89;
    const A4_HEIGHT = 595.28;
    const lateralMargin = 30; // margem lateral de 30 pontos

    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);
    const fonte = await pdfDoc.embedFont(fontBytes);
    const jpgImage = await pdfDoc.embedJpg(imgBytes);
    const logoImage = await pdfDoc.embedPng(logoBytes);
    // Dimensões originais da imagem de fundo
    const originalWidth = jpgImage.width;
    const originalHeight = jpgImage.height;

    // Calcular escala para caber na folha A4, com margem e redução de 10%
    const maxImageWidth = A4_WIDTH - 2 * lateralMargin;
    const maxImageHeight = A4_HEIGHT - 2 * lateralMargin;
    let scale = Math.min(
      maxImageWidth / originalWidth,
      maxImageHeight / originalHeight
    );
    scale = scale * 0.9; // reduzir a imagem em 10%
    const imageWidth = originalWidth * scale;
    const imageHeight = originalHeight * scale;
    const imageX = (A4_WIDTH - imageWidth) / 2;
    const imageY = (A4_HEIGHT - imageHeight) / 2;

    // Aumentar elementos em 20%
    const elementScale = (scale * 1.2) / scale; // equivale a 1.2

    const fontSizeTitulo = 16 * elementScale;
    const fontSizeNome = 8 * elementScale;
    const fontSizeAcademia = 7 * elementScale;
    const cor = rgb(0, 0, 0);
    // Cores do design system Torneio Fácil
    const navy = rgb(0.102, 0.133, 0.322);      // #1a2252
    const navyLight = rgb(0.141, 0.188, 0.439);  // #243070
    const red = rgb(0.894, 0.125, 0.173);         // #e4202c
    const gray500 = rgb(0.42, 0.455, 0.565);      // #6b7490
    const gray300 = rgb(0.769, 0.788, 0.847);     // #c4c9d8

    // Dimensões do logo
    const logoWidth = 90 * elementScale;
    const logoHeight = (logoImage.height / logoImage.width) * logoWidth;
    const logoX = (A4_WIDTH - logoWidth) / 2;
    // Header mais para cima
    const headerTopY = A4_HEIGHT - 10 * elementScale;
    const logoHeaderY = headerTopY - logoHeight;
    const line1Y = logoHeaderY - 5 * elementScale;
    const tournamentNameY = line1Y - 18 * elementScale;
    const line2Y = tournamentNameY - 15 * elementScale;
    const rulesY = line2Y - 18 * elementScale;

    // Dimensões do quadrado de assinatura
    const signBoxWidth = 200 * elementScale * 0.8; // reduzido em 20%
    const signBoxHeight = 90 * elementScale * 0.7; // reduzido em 20%
    const signBoxX = A4_WIDTH - signBoxWidth - lateralMargin - 40;

    const filteredAndSplitData = filterAndSplitBracketsData();

    // Ordenar as categorias
    const categoriasOrdenadas = sortCategoriesForPdf(
      Object.entries(filteredAndSplitData)
    );

    // Cada entrada em categoriasOrdenadas = 1 página (Parte1/Parte2 já são entradas separadas)
    const totalPaginasPdf = categoriasOrdenadas.length;

    // Posições do bracket-32 que pertencem a cada parte
    const parte1Positions = new Set([1, 17, 9, 25, 5, 21, 13, 29, 3, 19, 11, 27, 7, 23, 15, 31]);
    const parte2Positions = new Set([2, 18, 10, 26, 6, 22, 14, 30, 4, 20, 12, 28, 8, 24, 16, 32]);

    // Mapeamento: posição no bracket-32 → slot visual no layout de 16
    // Parte 1: posições ímpares do bracket mapeiam para os 16 slots visuais
    const posicao32ToSlot16Parte1: { [key: number]: number } = {
      // Esquerda: [1,17], [9,25], [5,21], [13,29] → slots esquerda do posicoesMap16
      1: 1, 17: 9, 9: 5, 25: 13, 5: 3, 21: 11, 13: 7, 29: 15,
      // Direita: [3,19], [11,27], [7,23], [15,31] → slots direita do posicoesMap16
      3: 2, 19: 10, 11: 6, 27: 14, 7: 4, 23: 12, 15: 8, 31: 16,
    };
    const posicao32ToSlot16Parte2: { [key: number]: number } = {
      // Esquerda: [2,18], [10,26], [6,22], [14,30]
      2: 1, 18: 9, 10: 5, 26: 13, 6: 3, 22: 11, 14: 7, 30: 15,
      // Direita: [4,20], [12,28], [8,24], [16,32]
      4: 2, 20: 10, 12: 6, 28: 14, 8: 4, 24: 12, 16: 8, 32: 16,
    };

    let currentPage = 1;

    for (const [categoria, estrutura] of categoriasOrdenadas) {
      const data = estrutura as any;
      const is32 = data.is32Bracket === true;
      const isPartTwo = data.isPartTwo === true;

      // Coletar atletas preservando posicao
      const todosAtletas: any[] = [];
      data.matchStructure.flat().forEach((match: any) => {
        if (match.lutador?.name) {
          todosAtletas.push({
            name: match.lutador.name,
            gym: match.lutador.gym || "",
            posicao: match.posicao,
          });
        }
      });

      // Filtrar atletas para esta página (Parte1 ou Parte2 para 32-brackets)
      let atletasPagina: any[];
      let posicaoToSlot: { [key: number]: number } | null = null;
      if (is32) {
        const positionsForThisPage = isPartTwo ? parte2Positions : parte1Positions;
        atletasPagina = todosAtletas.filter((a: any) => positionsForThisPage.has(a.posicao));
        posicaoToSlot = isPartTwo ? posicao32ToSlot16Parte2 : posicao32ToSlot16Parte1;
      } else {
        atletasPagina = todosAtletas;
      }

      const page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT]);

      // Fundo centralizado
      page.drawImage(jpgImage, {
        x: imageX,
        y: imageY,
        width: imageWidth,
        height: imageHeight,
      });

      // HEADER PDF ajustado
      // Logo centralizado e maior
      page.drawImage(logoImage, {
        x: logoX,
        y: logoHeaderY,
        width: logoWidth,
        height: logoHeight,
      });
      // Linha horizontal superior (navy)
      page.drawLine({
        start: { x: imageX, y: line1Y },
        end: { x: imageX + imageWidth, y: line1Y },
        thickness: 2 * elementScale,
        color: navy,
      });
      // Nome do torneio centralizado (navy)
      page.drawText(tournamentName, {
        x:
          imageX +
          (imageWidth -
            fonte.widthOfTextAtSize(
              tournamentName,
              14 * elementScale * 0.8
            )) /
          2,
        y: tournamentNameY,
        size: 14 * elementScale * 0.8,
        font: fonte,
        color: navy,
      });
      // Linha horizontal inferior (red)
      page.drawLine({
        start: { x: imageX, y: line2Y },
        end: { x: imageX + imageWidth, y: line2Y },
        thickness: 1.5 * elementScale,
        color: red,
      });
      // Regras centralizadas
      const desc = getDescriptionFromCategory(categoria);
      const rulesDescriptionText = `${categoria.replace(/-Parte\d+/i, "")}, ${desc.ageDescription
        }, ${desc.pesoDescription}`.toUpperCase();
      const fightTimeText =
        `Tempo de Luta: ${desc.fightTime} min`.toUpperCase();
      page.drawText(rulesDescriptionText, {
        x:
          imageX +
          (imageWidth -
            fonte.widthOfTextAtSize(
              rulesDescriptionText,
              10 * elementScale
            )) /
          2,
        y: rulesY,
        size: 10 * elementScale,
        font: fonte,
        color: navy,
      });

      const fightTimeY = rulesY - 10 * elementScale - 5 * elementScale;

      // Tempo de luta centralizado (navy)
      page.drawText(fightTimeText, {
        x:
          imageX +
          (imageWidth -
            fonte.widthOfTextAtSize(fightTimeText, 10 * elementScale)) /
          2,
        y: fightTimeY,
        size: 10 * elementScale,
        font: fonte,
        color: navy,
      });

      // Label "Parte 1" / "Parte 2" para brackets de 32
      if (is32) {
        const parteText = `(Parte ${isPartTwo ? "2" : "1"})`;
        const parteY = fightTimeY - 12 * elementScale;
        page.drawText(parteText, {
          x: imageX + (imageWidth - fonte.widthOfTextAtSize(parteText, 10 * elementScale)) / 2,
          y: parteY,
          size: 10 * elementScale,
          font: fonte,
          color: gray500,
        });
      }

      // Fatores de escala para ajustar as posições dos nomes dos lutadores
      const scaleX = imageWidth / originalWidth;
      const scaleY = imageHeight / originalHeight;

      // Desenhar números das posições em todos os 16 slots visuais
      const allSlots = Object.entries(posicoesMap16);
      allSlots.forEach(([slotKey, pos]) => {
        // Descobrir qual posição real do bracket corresponde a este slot
        let bracketPos: number;
        if (is32 && posicaoToSlot) {
          // Encontrar a posição do bracket que mapeia para este slot
          const mapping = isPartTwo ? posicao32ToSlot16Parte2 : posicao32ToSlot16Parte1;
          const entry = Object.entries(mapping).find(([, v]) => v === Number(slotKey));
          bracketPos = entry ? Number(entry[0]) : Number(slotKey);
        } else {
          bracketPos = Number(slotKey);
        }

        const posX = imageX + pos.x * scaleX;
        const posY = imageY + pos.y * scaleY - 6 * elementScale;
        // Número à esquerda do nome (lado esquerdo) ou à direita (lado direito)
        const isRightSide = pos.x > 500;
        const numText = bracketPos.toString();
        const numX = isRightSide
          ? imageX + imageWidth + 10  // fora do box, margem direita da imagem
          : posX - 15 * elementScale; // antes do box no lado esquerdo
        page.drawText(numText, {
          x: numX,
          y: posY + 2 * elementScale,
          size: 7 * elementScale,
          font: fonte,
          color: navyLight,
        });
      });

      // Desenhar atletas nas posições corretas
      atletasPagina.forEach((atleta: any) => {
        // Mapear posição do bracket para slot visual no layout de 16
        const slot = is32 && posicaoToSlot
          ? posicaoToSlot[atleta.posicao]
          : atleta.posicao;
        const pos = posicoesMap16[slot];
        if (!pos) return;

        const posX = imageX + pos.x * scaleX;
        const posY = imageY + pos.y * scaleY - 6 * elementScale;
        const MAX_CHARS = 31;
        const truncate = (text: string) =>
          text.length > MAX_CHARS ? text.slice(0, MAX_CHARS - 3) + "..." : text;
        page.drawText(truncate(atleta.name), {
          x: posX,
          y: posY,
          size: fontSizeNome,
          font: fonte,
          color: cor,
        });
        const truncateGym = (gym: string) => {
          const full = `(${gym})`;
          if (full.length > 36) return `(${gym.slice(0, 32)}...)`;
          return full;
        };
        page.drawText(truncateGym(atleta.gym), {
          x: posX,
          y: posY - 11 * elementScale,
          size: fontSizeAcademia,
          font: fonte,
          color: gray500,
        });
      });

      // RODAPÉ sempre no final da página
      const footerY = 25 * elementScale;
      // Caixa de assinatura mais próxima do rodapé
      const signBoxY = footerY + 5 * elementScale;
      page.drawRectangle({
        x: signBoxX,
        y: signBoxY,
        width: signBoxWidth,
        height: signBoxHeight,
        borderColor: navy,
        borderWidth: 0.75 * elementScale,
      });
      // Texto "Responsável:" em destaque
      const responsavelText = "Responsável:";
      page.drawText(responsavelText, {
        x: signBoxX + 10 * elementScale,
        y: signBoxY + signBoxHeight - 20,
        size: 12,
        font: fonte,
        color: navy,
      });
      // Nome:
      const nomeText = "Nome:";
      page.drawText(nomeText, {
        x: signBoxX + 10 * elementScale,
        y: signBoxY + signBoxHeight - 40,
        size: 12,
        font: fonte,
        color: navy,
      });
      // Linha para nome
      page.drawLine({
        start: {
          x: signBoxX + 50 * elementScale,
          y: signBoxY + signBoxHeight - 42,
        },
        end: {
          x: signBoxX + signBoxWidth - 10 * elementScale,
          y: signBoxY + signBoxHeight - 42,
        },
        thickness: 0.5 * elementScale,
        color: gray300,
      });
      // Assinatura:
      const assinaturaText = "Assinatura:";
      page.drawText(assinaturaText, {
        x: signBoxX + 10 * elementScale,
        y: signBoxY + signBoxHeight - 60,
        size: 12,
        font: fonte,
        color: navy,
      });
      // Linha para assinatura
      page.drawLine({
        start: {
          x: signBoxX + 65 * elementScale,
          y: signBoxY + signBoxHeight - 62,
        },
        end: {
          x: signBoxX + signBoxWidth - 10 * elementScale,
          y: signBoxY + signBoxHeight - 62,
        },
        thickness: 0.5 * elementScale,
        color: gray300,
      });
      // Linha horizontal do rodapé (navy)
      page.drawLine({
        start: { x: imageX, y: footerY },
        end: { x: imageX + imageWidth, y: footerY },
        thickness: 1 * elementScale,
        color: navy,
      });
      // Texto local e data à esquerda
      const leftFooter = `${tournamentLocation} , ${tournamentDate
        .split("-")
        .reverse()
        .join("/")}`;
      page.drawText(leftFooter, {
        x: imageX + 2 * elementScale,
        y: footerY - 18 * elementScale,
        size: 10 * elementScale,
        font: fonte,
        color: gray500,
      });
      // Texto site e página à direita
      const rightFooter = `www.torneiofacil.com.br ${currentPage}/${totalPaginasPdf}`;
      const rightFooterWidth = fonte.widthOfTextAtSize(
        rightFooter,
        10 * elementScale
      );
      page.drawText(rightFooter, {
        x: imageX + imageWidth - rightFooterWidth,
        y: footerY - 18 * elementScale,
        size: 10 * elementScale,
        font: fonte,
        color: gray500,
      });
      currentPage++;

      // Linhas dos classificados: mais próximas do rodapé e ordem 3, 2, 1 de cima para baixo
      const linhasYBase = footerY + signBoxHeight - 40; // mais próximo do rodapé
      const linhaWidth = 180 * elementScale;
      const numeroOffsetX = -22 * elementScale;
      // Cores dos classificados: 1º=dourado, 2º=cinza, 3º=bronze
      const podiumColors = [
        rgb(0.85, 0.65, 0.13),  // 1º - dourado
        gray500,                 // 2º - prata/cinza
        rgb(0.72, 0.45, 0.20),  // 3º - bronze
      ];
      for (let i = 0; i < 3; i++) {
        const yLinha = linhasYBase + (2 - i) * 24;
        page.drawLine({
          start: { x: A4_WIDTH / 2 - linhaWidth / 2, y: yLinha },
          end: { x: A4_WIDTH / 2 + linhaWidth / 2, y: yLinha },
          thickness: 0.6 * elementScale,
          color: gray300,
        });
        page.drawText(`${i + 1}.`, {
          x: A4_WIDTH / 2 - linhaWidth / 2 + numeroOffsetX,
          y: yLinha - 3 * elementScale,
          size: 10 * elementScale,
          font: fonte,
          color: podiumColors[i],
        });
      }
    }

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    //const blob = new Blob([pdfBytes.buffer], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    // link.download = "chaves_com_imagem.pdf";
    link.download = `CHAVES - ${tournamentName}.pdf`; // ✅ nome dinâmico com o torneio
    link.click();
  };

  const generateBlankPdf = async () => {
    // Carregar imagem de fundo
    const imgBytes = await fetch("/chave_limpa.jpg").then((res) =>
      res.arrayBuffer()
    );
    const fontBytes = await fetch("/DejaVuSans.ttf").then((res) =>
      res.arrayBuffer()
    );
    const logoBytes = await fetch(logo).then((res) => res.arrayBuffer());

    const A4_WIDTH = 841.89;
    const A4_HEIGHT = 595.28;
    const lateralMargin = 30;

    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);
    const fonte = await pdfDoc.embedFont(fontBytes);
    const jpgImage = await pdfDoc.embedJpg(imgBytes);
    const logoImage = await pdfDoc.embedPng(logoBytes);
    const originalWidth = jpgImage.width;
    const originalHeight = jpgImage.height;

    const maxImageWidth = A4_WIDTH - 2 * lateralMargin;
    const maxImageHeight = A4_HEIGHT - 2 * lateralMargin;
    let scale = Math.min(
      maxImageWidth / originalWidth,
      maxImageHeight / originalHeight
    );
    scale = scale * 0.9;
    const imageWidth = originalWidth * scale;
    const imageHeight = originalHeight * scale;
    const imageX = (A4_WIDTH - imageWidth) / 2;
    const imageY = (A4_HEIGHT - imageHeight) / 2;

    const elementScale = 1.2;
    const cor = rgb(0, 0, 0);
    const navy = rgb(0.102, 0.133, 0.322);
    const navyLight = rgb(0.141, 0.188, 0.439);
    const red = rgb(0.894, 0.125, 0.173);
    const gray500 = rgb(0.42, 0.455, 0.565);
    const gray300 = rgb(0.769, 0.788, 0.847);

    const logoWidth = 90 * elementScale;
    const logoHeight = (logoImage.height / logoImage.width) * logoWidth;
    const logoX = (A4_WIDTH - logoWidth) / 2;
    const headerTopY = A4_HEIGHT - 10 * elementScale;
    const logoHeaderY = headerTopY - logoHeight;
    const line1Y = logoHeaderY - 5 * elementScale;

    const signBoxWidth = 200 * elementScale * 0.8;
    const signBoxHeight = 90 * elementScale * 0.7;
    const signBoxX = A4_WIDTH - signBoxWidth - lateralMargin - 40;

    const numPages = 1;

    for (let p = 0; p < numPages; p++) {
      const page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT]);

      // Fundo
      page.drawImage(jpgImage, {
        x: imageX, y: imageY, width: imageWidth, height: imageHeight,
      });

      // Logo
      page.drawImage(logoImage, {
        x: logoX, y: logoHeaderY, width: logoWidth, height: logoHeight,
      });

      // Linha superior (navy)
      page.drawLine({
        start: { x: imageX, y: line1Y },
        end: { x: imageX + imageWidth, y: line1Y },
        thickness: 2 * elementScale,
        color: navy,
      });

      // Nome do torneio centralizado (navy) — igual ao PDF com fundo
      const tournamentNameY = line1Y - 18 * elementScale;
      page.drawText(tournamentName, {
        x: imageX + (imageWidth - fonte.widthOfTextAtSize(tournamentName, 14 * elementScale * 0.8)) / 2,
        y: tournamentNameY,
        size: 14 * elementScale * 0.8,
        font: fonte,
        color: navy,
      });

      // Linha inferior (red)
      const line2Y = tournamentNameY - 15 * elementScale;
      page.drawLine({
        start: { x: imageX, y: line2Y },
        end: { x: imageX + imageWidth, y: line2Y },
        thickness: 1.5 * elementScale,
        color: red,
      });

      // CATEGORIA_________________, ATÉ_______KG (centralizado, abaixo da linha vermelha)
      const catLabelY = line2Y - 16 * elementScale;
      const catText = "CATEGORIA";
      const catLineWidth = 320;
      const ateText = ", ATÉ";
      const kgLineWidth = 60;
      const kgText = "KG";
      const fontSize = 10 * elementScale;
      const catW = fonte.widthOfTextAtSize(catText, fontSize);
      const ateW = fonte.widthOfTextAtSize(ateText, fontSize);
      const kgW = fonte.widthOfTextAtSize(kgText, fontSize);
      const catTotalWidth = catW + catLineWidth + 5 + ateW + kgLineWidth + 3 + kgW;
      const catStartX = imageX + (imageWidth - catTotalWidth) / 2;

      page.drawText(catText, {
        x: catStartX, y: catLabelY, size: fontSize, font: fonte, color: navy,
      });
      const afterCat = catStartX + catW;
      page.drawLine({
        start: { x: afterCat, y: catLabelY - 2 },
        end: { x: afterCat + catLineWidth, y: catLabelY - 2 },
        thickness: 0.5 * elementScale, color: gray300,
      });
      const afterCatLine = afterCat + catLineWidth + 5;
      page.drawText(ateText, {
        x: afterCatLine, y: catLabelY, size: fontSize, font: fonte, color: navy,
      });
      const afterAte = afterCatLine + ateW;
      page.drawLine({
        start: { x: afterAte, y: catLabelY - 2 },
        end: { x: afterAte + kgLineWidth, y: catLabelY - 2 },
        thickness: 0.5 * elementScale, color: gray300,
      });
      page.drawText(kgText, {
        x: afterAte + kgLineWidth + 3, y: catLabelY, size: fontSize, font: fonte, color: navy,
      });

      // TEMPO DE LUTA: _______MIN (centralizado, abaixo da categoria)
      const tempoY = catLabelY - 16 * elementScale;
      const tempoLabel = "TEMPO DE LUTA:";
      const tempoLineWidth = 60;
      const minText = "MIN";
      const tempoLabelWidth = fonte.widthOfTextAtSize(tempoLabel, 10 * elementScale);
      const tempoTotalWidth = tempoLabelWidth + 5 + tempoLineWidth + 5 + fonte.widthOfTextAtSize(minText, 10 * elementScale);
      const tempoStartX = imageX + (imageWidth - tempoTotalWidth) / 2;

      page.drawText(tempoLabel, {
        x: tempoStartX, y: tempoY, size: 10 * elementScale, font: fonte, color: navy,
      });
      page.drawLine({
        start: { x: tempoStartX + tempoLabelWidth + 5, y: tempoY - 2 },
        end: { x: tempoStartX + tempoLabelWidth + 5 + tempoLineWidth, y: tempoY - 2 },
        thickness: 0.5 * elementScale, color: gray300,
      });
      page.drawText(minText, {
        x: tempoStartX + tempoLabelWidth + 5 + tempoLineWidth + 5, y: tempoY,
        size: 10 * elementScale, font: fonte, color: navy,
      });

      // Números das posições (1-16)
      const scaleX = imageWidth / originalWidth;
      const scaleY2 = imageHeight / originalHeight;
      Object.entries(posicoesMap16).forEach(([slotKey, pos]) => {
        const posX = imageX + pos.x * scaleX;
        const posY = imageY + pos.y * scaleY2 - 6 * elementScale;
        const isRightSide = pos.x > 500;
        const numX = isRightSide
          ? imageX + imageWidth + 2
          : posX - 15 * elementScale;
        page.drawText(slotKey, {
          x: numX, y: posY + 2 * elementScale,
          size: 7 * elementScale, font: fonte, color: navyLight,
        });
      });

      // RODAPÉ
      const footerY = 25 * elementScale;

      // Caixa de assinatura
      const signBoxY = footerY + 5 * elementScale;
      page.drawRectangle({
        x: signBoxX, y: signBoxY, width: signBoxWidth, height: signBoxHeight,
        borderColor: navy, borderWidth: 0.75 * elementScale,
      });
      page.drawText("Responsável:", {
        x: signBoxX + 10 * elementScale, y: signBoxY + signBoxHeight - 20,
        size: 12, font: fonte, color: navy,
      });
      page.drawText("Nome:", {
        x: signBoxX + 10 * elementScale, y: signBoxY + signBoxHeight - 40,
        size: 12, font: fonte, color: navy,
      });
      page.drawLine({
        start: { x: signBoxX + 50 * elementScale, y: signBoxY + signBoxHeight - 42 },
        end: { x: signBoxX + signBoxWidth - 10 * elementScale, y: signBoxY + signBoxHeight - 42 },
        thickness: 0.5 * elementScale, color: gray300,
      });
      page.drawText("Assinatura:", {
        x: signBoxX + 10 * elementScale, y: signBoxY + signBoxHeight - 60,
        size: 12, font: fonte, color: navy,
      });
      page.drawLine({
        start: { x: signBoxX + 65 * elementScale, y: signBoxY + signBoxHeight - 62 },
        end: { x: signBoxX + signBoxWidth - 10 * elementScale, y: signBoxY + signBoxHeight - 62 },
        thickness: 0.5 * elementScale, color: gray300,
      });

      // Linha do rodapé
      page.drawLine({
        start: { x: imageX, y: footerY },
        end: { x: imageX + imageWidth, y: footerY },
        thickness: 1 * elementScale, color: navy,
      });

      // Textos do rodapé
      const leftFooter = `${tournamentLocation} , ${tournamentDate.split("-").reverse().join("/")}`;
      page.drawText(leftFooter, {
        x: imageX + 2 * elementScale, y: footerY - 18 * elementScale,
        size: 10 * elementScale, font: fonte, color: gray500,
      });
      const rightFooter = `www.torneiofacil.com.br ${p + 1}/${numPages}`;
      const rightFooterWidth = fonte.widthOfTextAtSize(rightFooter, 10 * elementScale);
      page.drawText(rightFooter, {
        x: imageX + imageWidth - rightFooterWidth, y: footerY - 18 * elementScale,
        size: 10 * elementScale, font: fonte, color: gray500,
      });

      // Linhas dos classificados (1º, 2º, 3º)
      const linhasYBase = footerY + signBoxHeight - 40;
      const linhaWidth = 180 * elementScale;
      const numeroOffsetX = -22 * elementScale;
      const podiumColors = [
        rgb(0.85, 0.65, 0.13), gray500, rgb(0.72, 0.45, 0.20),
      ];
      for (let i = 0; i < 3; i++) {
        const yLinha = linhasYBase + (2 - i) * 24;
        page.drawLine({
          start: { x: A4_WIDTH / 2 - linhaWidth / 2, y: yLinha },
          end: { x: A4_WIDTH / 2 + linhaWidth / 2, y: yLinha },
          thickness: 0.6 * elementScale, color: gray300,
        });
        page.drawText(`${i + 1}.`, {
          x: A4_WIDTH / 2 - linhaWidth / 2 + numeroOffsetX, y: yLinha - 3 * elementScale,
          size: 10 * elementScale, font: fonte, color: podiumColors[i],
        });
      }
    }

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `CHAVES EM BRANCO - ${tournamentName}.pdf`;
    link.click();
  };

  const generateWeighInPdf = async () => {
    const fontBytes = await fetch("/DejaVuSans.ttf").then((res) =>
      res.arrayBuffer()
    );
    const logoBytes = await fetch(logo).then((res) => res.arrayBuffer());

    const A4_WIDTH = 595.28;
    const A4_HEIGHT = 841.89;
    const margin = 25;

    const pdfDoc = await PDFDocument.create();
    pdfDoc.registerFontkit(fontkit);
    const fonte = await pdfDoc.embedFont(fontBytes);
    const logoImage = await pdfDoc.embedPng(logoBytes);

    const navy = rgb(0.102, 0.133, 0.322);
    const red = rgb(0.894, 0.125, 0.173);
    const gray500 = rgb(0.42, 0.455, 0.565);
    const gray300 = rgb(0.769, 0.788, 0.847);
    const gray50 = rgb(0.969, 0.973, 0.984);
    const white = rgb(1, 1, 1);

    const contentWidth = A4_WIDTH - 2 * margin;
    const logoWidth = 70;
    const logoHeight = (logoImage.height / logoImage.width) * logoWidth;

    // Agrupar atletas por categoria (usando bracketsData original, sem split)
    const categoriasOrdenadas = sortCategoriesForPdf(
      Object.entries(bracketsData)
    );

    let page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT]);
    let curY = A4_HEIGHT - margin;

    const addNewPage = () => {
      page = pdfDoc.addPage([A4_WIDTH, A4_HEIGHT]);
      curY = A4_HEIGHT - margin;
    };

    const drawPageHeader = () => {
      // Logo à esquerda + Nome do torneio centralizado + Subtítulo
      const smallLogoW = 95;
      const smallLogoH = (logoImage.height / logoImage.width) * smallLogoW;
      const headerH = Math.max(smallLogoH, 28);

      // Logo alinhada à esquerda
      page.drawImage(logoImage, {
        x: margin,
        y: curY - smallLogoH,
        width: smallLogoW,
        height: smallLogoH,
      });

      // Nome do torneio centralizado na página
      const titleSize = 11;
      const titleW = fonte.widthOfTextAtSize(tournamentName, titleSize);
      page.drawText(tournamentName, {
        x: (A4_WIDTH - titleW) / 2,
        y: curY - 12,
        size: titleSize,
        font: fonte,
        color: navy,
      });

      // Subtítulo centralizado
      const subtitle = "REGISTRO DE PESAGEM";
      const subW = fonte.widthOfTextAtSize(subtitle, 8);
      page.drawText(subtitle, {
        x: (A4_WIDTH - subW) / 2,
        y: curY - 24,
        size: 8,
        font: fonte,
        color: gray500,
      });

      curY -= headerH + 6;

      // Linha red
      page.drawLine({
        start: { x: margin, y: curY },
        end: { x: A4_WIDTH - margin, y: curY },
        thickness: 1.5,
        color: red,
      });
      curY -= 16;
    };

    const drawFooter = (pageNum: number, totalPages: number) => {
      const footerY = 25;
      page.drawLine({
        start: { x: margin, y: footerY },
        end: { x: A4_WIDTH - margin, y: footerY },
        thickness: 1,
        color: navy,
      });
      const leftText = `${tournamentLocation} , ${tournamentDate.split("-").reverse().join("/")}`;
      page.drawText(leftText, {
        x: margin,
        y: footerY - 12,
        size: 7,
        font: fonte,
        color: gray500,
      });
      const rightText = `www.torneiofacil.com.br ${pageNum}/${totalPages}`;
      const rw = fonte.widthOfTextAtSize(rightText, 7);
      page.drawText(rightText, {
        x: A4_WIDTH - margin - rw,
        y: footerY - 12,
        size: 7,
        font: fonte,
        color: gray500,
      });
    };

    // Pré-calcular total de páginas (estimativa)
    // Vamos gerar primeiro e contar depois, usando placeholder
    const pages: any[] = [];

    drawPageHeader();

    // Colunas da tabela
    const colNumW = 25;
    const colNameW = contentWidth * 0.42;
    const colGymW = contentWidth * 0.35;
    const colWeightW = contentWidth - colNumW - colNameW - colGymW;
    const rowHeight = 18;
    const headerRowHeight = 16;
    const fontSize = 8;
    const headerFontSize = 7;

    for (const [categoria, estrutura] of categoriasOrdenadas) {
      const data = estrutura as any;
      const desc = getDescriptionFromCategory(categoria);

      // Coletar atletas únicos da categoria
      const atletasMap = new Map();
      data.matchStructure.flat().forEach((match: any) => {
        if (match.lutador?.name) {
          atletasMap.set(match.lutador.name, {
            name: match.lutador.name,
            gym: match.lutador.gym || "Sem academia",
          });
        }
      });
      const atletas = Array.from(atletasMap.values()).sort((a: any, b: any) =>
        a.name.localeCompare(b.name)
      );

      if (atletas.length === 0) continue;

      // Espaço necessário: header da categoria + header tabela + pelo menos 1 linha
      const spaceNeeded = 40 + headerRowHeight + rowHeight;
      if (curY - spaceNeeded < 50) {
        addNewPage();
        drawPageHeader();
      }

      // Header da categoria (duas partes na mesma linha, alinhado à esquerda)
      const catClean = categoria.replace(/-parte\d+/gi, "");
      const catName = catClean.toUpperCase();
      const catRules = ` ${desc.ageDescription}, ${desc.pesoDescription}, tempo de luta: ${desc.fightTime} min`;

      // Categoria em navy bold (9px)
      page.drawText(catName, {
        x: margin,
        y: curY,
        size: 9,
        font: fonte,
        color: navy,
      });
      // Regras em gray (7px) continuando na mesma linha
      const catNameW = fonte.widthOfTextAtSize(catName, 9);
      page.drawText(catRules, {
        x: margin + catNameW,
        y: curY,
        size: 7,
        font: fonte,
        color: gray500,
      });
      curY -= 10;

      // Header da tabela
      const tableX = margin;
      page.drawRectangle({
        x: tableX,
        y: curY - headerRowHeight,
        width: contentWidth,
        height: headerRowHeight,
        color: navy,
      });

      const headers = ["Nº", "ATLETA", "ACADEMIA", "PESO (KG)"];
      const colWidths = [colNumW, colNameW, colGymW, colWeightW];
      let hx = tableX;
      headers.forEach((h, i) => {
        const hw = fonte.widthOfTextAtSize(h, headerFontSize);
        const cellCenter = hx + (colWidths[i] - hw) / 2;
        page.drawText(h, {
          x: cellCenter,
          y: curY - headerRowHeight + 5,
          size: headerFontSize,
          font: fonte,
          color: white,
        });
        hx += colWidths[i];
      });
      curY -= headerRowHeight;

      // Linhas dos atletas
      atletas.forEach((atleta: any, idx: number) => {
        if (curY - rowHeight < 50) {
          addNewPage();
          drawPageHeader();
          // Repetir header da tabela na nova página
          page.drawRectangle({
            x: tableX,
            y: curY - headerRowHeight,
            width: contentWidth,
            height: headerRowHeight,
            color: navy,
          });
          let hx2 = tableX;
          headers.forEach((h, i) => {
            const hw = fonte.widthOfTextAtSize(h, headerFontSize);
            const cellCenter = hx2 + (colWidths[i] - hw) / 2;
            page.drawText(h, {
              x: cellCenter,
              y: curY - headerRowHeight + 5,
              size: headerFontSize,
              font: fonte,
              color: white,
            });
            hx2 += colWidths[i];
          });
          curY -= headerRowHeight;
        }

        // Fundo alternado
        const bgColor = idx % 2 === 0 ? gray50 : white;
        page.drawRectangle({
          x: tableX,
          y: curY - rowHeight,
          width: contentWidth,
          height: rowHeight,
          color: bgColor,
        });

        // Bordas laterais e inferior
        page.drawLine({
          start: { x: tableX, y: curY - rowHeight },
          end: { x: tableX + contentWidth, y: curY - rowHeight },
          thickness: 0.3,
          color: gray300,
        });

        // Separadores de coluna
        let cx = tableX;
        colWidths.forEach((w) => {
          cx += w;
          page.drawLine({
            start: { x: cx, y: curY },
            end: { x: cx, y: curY - rowHeight },
            thickness: 0.3,
            color: gray300,
          });
        });

        // Conteúdo das células
        const cellY = curY - rowHeight + 5;

        // Nº (centralizado)
        const numStr = (idx + 1).toString();
        const numW = fonte.widthOfTextAtSize(numStr, fontSize);
        page.drawText(numStr, {
          x: tableX + (colNumW - numW) / 2,
          y: cellY,
          size: fontSize,
          font: fonte,
          color: navy,
        });

        // Nome
        const nameText = atleta.name.length > 35
          ? atleta.name.slice(0, 32) + "..."
          : atleta.name;
        page.drawText(nameText, {
          x: tableX + colNumW + 5,
          y: cellY,
          size: fontSize,
          font: fonte,
          color: rgb(0, 0, 0),
        });

        // Academia
        const gymText = atleta.gym.length > 30
          ? atleta.gym.slice(0, 27) + "..."
          : atleta.gym;
        page.drawText(gymText, {
          x: tableX + colNumW + colNameW + 5,
          y: cellY,
          size: fontSize,
          font: fonte,
          color: gray500,
        });

        // Campo peso (linha para preenchimento manual)
        const pesoLineX = tableX + colNumW + colNameW + colGymW + 10;
        const pesoLineEndX = tableX + contentWidth - 10;
        page.drawLine({
          start: { x: pesoLineX, y: cellY - 1 },
          end: { x: pesoLineEndX, y: cellY - 1 },
          thickness: 0.5,
          color: gray300,
        });

        curY -= rowHeight;
      });

      curY -= 24; // espaço entre categorias
    }

    // Adicionar footers (pós-geração)
    const allPages = pdfDoc.getPages();
    const totalPages = allPages.length;
    allPages.forEach((p, i) => {
      const footerY = 25;
      p.drawLine({
        start: { x: margin, y: footerY },
        end: { x: A4_WIDTH - margin, y: footerY },
        thickness: 1,
        color: navy,
      });
      const leftText = `${tournamentLocation} , ${tournamentDate.split("-").reverse().join("/")}`;
      p.drawText(leftText, {
        x: margin,
        y: footerY - 12,
        size: 7,
        font: fonte,
        color: gray500,
      });
      const rightText = `www.torneiofacil.com.br ${i + 1}/${totalPages}`;
      const rw = fonte.widthOfTextAtSize(rightText, 7);
      p.drawText(rightText, {
        x: A4_WIDTH - margin - rw,
        y: footerY - 12,
        size: 7,
        font: fonte,
        color: gray500,
      });
    });

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `PESAGEM - ${tournamentName}.pdf`;
    link.click();
  };

  // Function to apply all active filters and split 32-person brackets
  const filterAndSplitBracketsData = () => {
    let currentFilteredData: any = {};

    Object.entries(bracketsData).forEach(([key, data]: any) => {
      // Apply fighter name and gym filters first
      const hasMatchingFighter = data.matchStructure.some((match: any[]) =>
        match.some((m: any) => {
          if (!m.lutador || typeof m.lutador !== "object") return false;
          const name = m.lutador.name?.toLowerCase() || "";
          const gym = m.lutador.gym?.toLowerCase() || "";

          const nameMatches =
            !fighterNameFilter ||
            name.includes(fighterNameFilter.toLowerCase());
          const gymMatches =
            !gymFilter || gym.includes(gymFilter.toLowerCase());

          return nameMatches && gymMatches;
        })
      );

      // Apply category filter
      const categoryMatches =
        !categoryFilter ||
        key.toLowerCase().includes(categoryFilter.toLowerCase());

      if (hasMatchingFighter && categoryMatches) {
        // Count actual unique competitors
        const competitorCount = new Set(
          data.matchStructure
            .flat()
            .filter(
              (c: any) =>
                c.lutador && typeof c.lutador === "object" && c.lutador.name
            )
            .map((c: any) => c.lutador.name)
        ).size;

        // If there are more than 16 actual competitors, treat as a 32-person bracket
        if (competitorCount > 16) {
          // Create two parts for the 32-person bracket
          currentFilteredData[`${key}-Parte1`] = {
            ...data,
            is32Bracket: true,
            isPartTwo: false,
            competitorsForPart: data.matchStructure,
          };
          currentFilteredData[`${key}-Parte2`] = {
            ...data,
            is32Bracket: true,
            isPartTwo: true,
            competitorsForPart: data.matchStructure,
          };
        } else {
          // For 16 or fewer competitors, render as a single bracket
          currentFilteredData[key] = {
            ...data,
            is32Bracket: false,
            isPartTwo: false,
            competitorsForPart: data.matchStructure,
          };
        }
      }
    });

    return currentFilteredData;
  };

  // Get the filtered and potentially split data to be displayed
  const filteredAndSplitData = filterAndSplitBracketsData();

  // Get unique categories from the original bracketsData for Autocomplete options
  const availableCategories = Object.keys(bracketsData || {}).sort();

  // Função para normalizar textos (copiada do brackets)
  const normalize = (text: string) =>
    text
      ?.normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/\s+/g, "");

  // Função para buscar as descrições das regras da categoria
  const getDescriptionFromCategory = (categoryString: string) => {
    const cleanCategoryString = categoryString
      ?.replace(/-parte\d+/gi, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();

    const parts = cleanCategoryString.split("/");
    if (parts.length < 3) {
      return {
        ageDescription: "--",
        pesoDescription: "--",
        fightTime: "--",
      };
    }
    const category = normalize(parts[0]);
    const gender = normalize(parts[1]);
    const belt = normalize(parts[2]);
    const pesoDesc = normalize(parts[3]);

    const matchCategory = tournamentRules?.find((rule: any) => {
      const jsonGender = normalize(rule.genero);
      const jsonCategory = normalize(rule.categoria);
      const jsonBelts = rule.faixa.map((f: any) => normalize(f.nome));
      const jsonPesoDesc = normalize(rule.peso.descricao);

      return (
        jsonGender === gender &&
        jsonCategory === category &&
        jsonBelts.some((b: string) => b == belt) &&
        jsonPesoDesc === pesoDesc
      );
    });

    if (matchCategory) {
      const beltFightTime = matchCategory.faixa?.find(
        (b: { nome: string }) => normalize(b.nome) == belt
      );

      return {
        ageDescription: matchCategory.idade.descricaoCompleta,
        pesoDescription: matchCategory.peso.max,
        fightTime: beltFightTime ? beltFightTime.tempodeLuta : "--",
      };
    }

    return {
      ageDescription: "--",
      pesoDescription: "--",
      fightTime: "--",
    };
  };

  // Função para ordenar as categorias no PDF
  const sortCategoriesForPdf = (categories: any[]) => {
    return categories.sort((a, b) => {
      const orderA = a[1].order;
      const orderB = b[1].order;

      return orderA - orderB;
    });
  };

  // Dicionários de posições para chave de 16 e 32 atletas
  // Coordenadas ajustadas para chave_limpa.jpg (3098x1455)
  const posicoesMap16: { [key: number]: { x: number; y: number } } = {
    1: { x: 12, y: 1416 },
    9: { x: 12, y: 1247 },
    5: { x: 12, y: 1053 },
    13: { x: 12, y: 884 },
    3: { x: 12, y: 622 },
    11: { x: 12, y: 460 },
    7: { x: 12, y: 266 },
    15: { x: 12, y: 102 },
    2: { x: 2275, y: 1416 },
    10: { x: 2275, y: 1247 },
    6: { x: 2275, y: 1053 },
    14: { x: 2275, y: 884 },
    4: { x: 2275, y: 622 },
    12: { x: 2275, y: 460 },
    8: { x: 2275, y: 266 },
    16: { x: 2275, y: 102 },
  };

  return (
    <div>
      <Button
        variant="contained"
        onClick={handleOpen}
        sx={{ backgroundColor: "#373737", fontFamily: "inter, sans serif" }}
      >
        Chaves
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-title" variant="h6" component="h2" gutterBottom>
            Chaves do Torneio: <strong>{tournamentName}</strong>
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <TextField
              label="Nome do Lutador"
              sx={{ flex: "1 1 300px" }}
              value={fighterNameFilter}
              onChange={(e) => setFighterNameFilter(e.target.value)}
            />
            <TextField
              label="Academia"
              sx={{ flex: "1 1 300px" }}
              value={gymFilter}
              onChange={(e) => setGymFilter(e.target.value)}
            />
            <Autocomplete
              sx={{ flex: "1 1 300px" }}
              options={availableCategories}
              value={categoryFilter}
              onChange={(event, newValue: string | null) => {
                setCategoryFilter(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Categoria" />
              )}
              clearOnEscape
            />
          </Box>

          <Box sx={{ overflowY: "auto", flexGrow: 1, p: 1 }}>
            {Object.keys(bracketsData || {}).length > 0 ? (
              <>
                {Object.keys(filteredAndSplitData).length > 0 ? (
                  sortCategoriesForPdf(
                    Object.entries(filteredAndSplitData)
                  ).map(([item, bracketInfo]: [string, any], index) => {
                    return (
                      <div
                        key={item}
                        ref={(el) => (bracketRefs.current[item] = el)}
                        style={{ marginBottom: "20px" }}
                      >
                        <TournamentBracket
                          competitors={bracketInfo?.competitorsForPart || []}
                          tournamentRules={tournamentRules}
                          category={item}
                          tournamentName={tournamentName}
                          tournamentDate={tournamentDate}
                          tournamentLocation={tournamentLocation}
                          index={index}
                          totalPages={Object.keys(filteredAndSplitData).length}
                          is32Bracket={bracketInfo.is32Bracket}
                          isPartTwo={bracketInfo.isPartTwo}
                        />
                      </div>
                    );
                  })
                ) : (
                  <Typography sx={{ mt: 2 }}>
                    Nenhuma chave encontrada com os filtros aplicados.
                  </Typography>
                )}
              </>
            ) : (
              // Show "Gerar Chaves" button if no brackets are present and user has permission
              account.id === 34 && (
                <>
                  <Button onClick={generateKeys} sx={{ mt: 2 }}>
                    Gerar Chaves
                  </Button>
                  <br />

                  <Button onClick={generateManualFights} sx={{ mt: 2 }}>
                    Gerar Lutas Manuais
                  </Button>

                  <br />
                  <Button onClick={generateGymPoints} sx={{ mt: 2 }}>
                    Gerar pontuação academias
                  </Button>
                </>
              )
            )}
          </Box>

          {account.id === 34 &&
            Object.keys(filteredAndSplitData).length > 0 && (
              <Box
                sx={{
                  mt: 3,
                  pt: 2,
                  borderTop: "1px solid rgba(26,34,82,0.08)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6b7490",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }}
                >
                  Exportar documentos
                </Typography>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    onClick={generatePdfWithImage}
                    size="small"
                    sx={{
                      backgroundColor: "#1a2252",
                      "&:hover": { backgroundColor: "#243070" },
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      borderRadius: "8px",
                      px: 2,
                    }}
                  >
                    Chaves com Fundo
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={generateBlankPdf}
                    size="small"
                    sx={{
                      borderColor: "#1a2252",
                      color: "#1a2252",
                      "&:hover": { backgroundColor: "rgba(26,34,82,0.04)", borderColor: "#243070" },
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      borderRadius: "8px",
                      px: 2,
                    }}
                  >
                    Chave em Branco
                  </Button>

                  {/* Separador visual */}
                  <Box sx={{ width: "1px", backgroundColor: "rgba(26,34,82,0.12)", mx: 0.5 }} />

                  {/* Relatórios */}
                  <Button
                    variant="contained"
                    onClick={generateWeighInPdf}
                    size="small"
                    sx={{
                      backgroundColor: "#e4202c",
                      "&:hover": { backgroundColor: "#c41820" },
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      borderRadius: "8px",
                      px: 2,
                    }}
                  >
                    Pesagem
                  </Button>

                  <Button
                    variant="contained"
                    onClick={generateManualFights}
                    size="small"
                    sx={{
                      backgroundColor: "#e4202c",
                      "&:hover": { backgroundColor: "#c41820" },
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      borderRadius: "8px",
                      px: 2,
                    }}
                  >
                    Lutas Manuais
                  </Button>

                  <Button
                    variant="contained"
                    onClick={generateGymPoints}
                    size="small"
                    sx={{
                      backgroundColor: "#e4202c",
                      "&:hover": { backgroundColor: "#c41820" },
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      borderRadius: "8px",
                      px: 2,
                    }}
                  >
                    Academia Campeã
                  </Button>
                </Box>
              </Box>
            )}

          <Box
            sx={{
              mt: 2,
              pt: 1.5,
              borderTop: "1px solid rgba(26,34,82,0.08)",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              size="small"
              sx={{
                borderColor: "#c4c9d8",
                color: "#6b7490",
                "&:hover": { backgroundColor: "rgba(26,34,82,0.04)", borderColor: "#1a2252", color: "#1a2252" },
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                borderRadius: "8px",
                px: 3,
              }}
            >
              Fechar
            </Button>
          </Box>
        </Box>
      </Modal>
      <ToastContainer position="top-center" />
    </div>
  );
}
